// Incoming-call ringtone catalogue + players.
// Built-in tones are WebAudio-synthesized (asset-free); 'custom' loops an uploaded audio file.

export const CALL_RINGTONE_OPTIONS = [
  { id: 'classic', label: 'Classic' },
  { id: 'digital', label: 'Digital' },
  { id: 'retro', label: 'Retro Phone' },
  { id: 'chime', label: 'Chime' },
  { id: 'gentle', label: 'Gentle' },
];

export const getStoredRingtoneChoice = () => localStorage.getItem('call_ringtone_choice') || 'classic';
export const setStoredRingtoneChoice = (v) => localStorage.setItem('call_ringtone_choice', v || 'classic');

// Module-level cache of the user's uploaded ringtone (object URL)
let customRingtoneUrl = null;
let customRingtoneLoading = null;

export const getCustomRingtoneUrl = () => customRingtoneUrl;

export const clearCustomRingtoneCache = () => {
  if (customRingtoneUrl) { try { URL.revokeObjectURL(customRingtoneUrl); } catch (e) {} }
  customRingtoneUrl = null;
  customRingtoneLoading = null;
};

export const loadCustomRingtoneUrl = async (api) => {
  if (customRingtoneUrl) return customRingtoneUrl;
  if (!customRingtoneLoading) {
    customRingtoneLoading = api
      .get('/settings/call-ringtone/file', { responseType: 'blob' })
      .then((res) => {
        customRingtoneUrl = URL.createObjectURL(res.data);
        return customRingtoneUrl;
      })
      .catch(() => { customRingtoneLoading = null; return null; });
  }
  return customRingtoneLoading;
};

const playNote = (ctx, freq, start, dur, peak = 0.35, type = 'sine') => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
  gain.gain.exponentialRampToValueAtTime(peak, ctx.currentTime + start + 0.03);
  gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
  osc.connect(gain).connect(ctx.destination);
  osc.start(ctx.currentTime + start);
  osc.stop(ctx.currentTime + start + dur + 0.05);
};

// One ring "cycle" per tone id + the loop interval (ms) between cycles.
const RING_PATTERNS = {
  classic: { every: 1600, play: (ctx) => playNote(ctx, 880, 0, 0.5, 0.4) },
  digital: { every: 1300, play: (ctx) => { playNote(ctx, 1046, 0, 0.14, 0.3, 'square'); playNote(ctx, 1046, 0.22, 0.14, 0.3, 'square'); } },
  retro: { every: 2600, play: (ctx) => { playNote(ctx, 440, 0, 1.0, 0.28); playNote(ctx, 480, 0, 1.0, 0.28); } },
  chime: { every: 2200, play: (ctx) => { playNote(ctx, 1318, 0, 0.35, 0.3); playNote(ctx, 1046, 0.3, 0.35, 0.3); playNote(ctx, 784, 0.6, 0.5, 0.3); } },
  gentle: { every: 2400, play: (ctx) => { playNote(ctx, 660, 0, 0.6, 0.18); playNote(ctx, 880, 0.5, 0.6, 0.12); } },
};

const vibrate = () => { if (navigator.vibrate) { try { navigator.vibrate([350, 200, 350]); } catch (e) {} } };
const stopVibrate = () => { if (navigator.vibrate) { try { navigator.vibrate(0); } catch (e) {} } };

const createBuiltinRinger = (id) => {
  const pattern = RING_PATTERNS[id] || RING_PATTERNS.classic;
  let ctx = null;
  let interval = null;
  const ring = () => {
    try {
      if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      pattern.play(ctx);
    } catch (e) { /* autoplay blocked — silent */ }
  };
  return {
    start: () => {
      if (interval) return;
      ring(); vibrate();
      interval = setInterval(() => { ring(); vibrate(); }, pattern.every);
    },
    stop: () => {
      if (interval) { clearInterval(interval); interval = null; }
      stopVibrate();
      if (ctx) { try { ctx.close(); } catch (e) {} ctx = null; }
    },
  };
};

const createCustomRinger = (url) => {
  let audio = null;
  let vibeInterval = null;
  return {
    start: () => {
      if (audio) return;
      audio = new Audio(url);
      audio.loop = true;
      audio.volume = 1.0;
      audio.play().catch(() => {});
      vibrate();
      vibeInterval = setInterval(vibrate, 1600);
    },
    stop: () => {
      if (audio) { try { audio.pause(); audio.src = ''; } catch (e) {} audio = null; }
      if (vibeInterval) { clearInterval(vibeInterval); vibeInterval = null; }
      stopVibrate();
    },
  };
};

// Build the ringer matching the user's choice; falls back to classic.
export const createRingtoneRinger = (choice, customUrl) => {
  if (choice === 'custom' && customUrl) return createCustomRinger(customUrl);
  return createBuiltinRinger(choice);
};

// Short preview for the Settings picker — returns a stop() function.
export const previewRingtone = (choice, customUrl) => {
  const ringer = createRingtoneRinger(choice, customUrl);
  ringer.start();
  const timer = setTimeout(() => ringer.stop(), 3200);
  return () => { clearTimeout(timer); ringer.stop(); };
};
